import { useState } from 'react';
import styles from './ResetPasswordModal.module.css';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { ResetPassword } from '../../../auth/_requests';
import passwordOff from '../../../assets/pages/login/eyeClosed.svg';
import passwordOn from '../../../assets/pages/login/eye.svg';
import { PasswordInput } from '../../UI/PasswordInput/PasswordInput';
import { motion } from 'framer-motion';
import { useModal } from '../../hooks/modalContext';
import { useAuth } from '../../../auth/Auth';
import { X } from 'lucide-react';
import { showToast } from '../../utils/showToast';

const loginSchema = Yup.object().shape({
  oldPassword: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(15, 'Maximum 15 characters')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\W_]/, 'Must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces')
    .required('Current Password is required'),
  password: Yup.string()
    .min(8, 'Minimum 8 characters')
    .max(15, 'Maximum 15 characters')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .matches(/[\W_]/, 'Must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces')
    .required('Password is required'),

  changePassword: Yup.string()
    .min(3, 'Minimum 8 characters')
    .max(50, 'Maximum 15 characters')
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
});

const initialValues = {
  oldPassword: '',
  password: '',
  changePassword: '',
};

export function ResetPasswordModal() {
  const { setModal } = useModal();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const requestBody = {
          passwordData: {
            oldPassword: values.oldPassword,
            newPassword: values.password,
            confirmPassword: values.changePassword,
          },
        };
        const response = await ResetPassword(auth?.data.token, requestBody);
        if (response?.message === 'success') {
          showToast({
            message: 'Password changed successfully!',
            type: 'success',
          });
          setModal(null);
        } else if (response?.message === 'invalid_password') {
          showToast({
            message: 'The current password is incorrect. Please check it and try again.',
            type: 'error',
          });
        } else {
          showToast({
            message: 'Password cannot be same as any of your last 4 used passwords.',
            type: 'error',
          });
        }
      } catch (error: any) {
        console.error('Error resetting password:', error);
        const backendMessage = error?.response?.data?.message;
        if (backendMessage === 'new_password_cannot_be_the_same_as_any_of_your_last_4_passwords') {
          showToast({
            message: 'New Password cannot be the same as any of your last 4 passwords',
            type: 'error',
          });
        } else if (backendMessage === 'invalid_password') {
          showToast({
            message: 'The current password is incorrect. Please check it and try again.',
            type: 'error',
          });
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <span> Reset password</span>{' '}
        <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
      </div>
      <div className={styles.contentOuter}>
        <form className={styles.form} onSubmit={formik.handleSubmit} noValidate>
          <div className={styles.formUpper}>
            <div className={styles.formBoxes}>
              <div className={styles.formBoxesInner}>
                <div
                  className={'inputWrapper'}
                  title='Enter your current password to create a new password'
                >
                  <div className={'label'}>
                    Current password<span className={styles.mandatory}>*</span>
                  </div>
                  <div
                    className={clsx(
                      formik.touched.oldPassword && formik.errors.oldPassword
                        ? styles.isInvalid
                        : '',
                      formik.touched.oldPassword && !formik.errors.oldPassword ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <PasswordInput
                      value={formik.values.password}
                      onChange={(newPassword: string) => {
                        formik.setFieldValue('oldPassword', newPassword);
                      }}
                      onBlur={formik.handleBlur('oldPassword')}
                      showPassword={oldPassword}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          formik.handleSubmit();
                        }
                      }}
                      isLogin={true}
                      placeholder='Enter current password'
                    />
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        setOldPassword(!oldPassword);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      {!oldPassword && <img src={passwordOff} alt='passwordOff' />}
                      {oldPassword && <img src={passwordOn} alt='passwordOn' />}
                    </button>
                  </div>
                  {formik.touched.oldPassword && formik.errors.oldPassword && (
                    <div className={styles.emailError}>
                      <span>{formik.errors.oldPassword}</span>
                    </div>
                  )}
                </div>
                <div
                  className={'inputWrapper'}
                  title='Password must be 8-15 characters long, including at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.'
                >
                  <div className={'label'}>
                    New password<span className={styles.mandatory}>*</span>
                  </div>
                  <div
                    className={clsx(
                      formik.touched.password && formik.errors.password ? styles.isInvalid : '',
                      formik.touched.password && !formik.errors.password ? styles.valid : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <PasswordInput
                      value={formik.values.password}
                      onChange={(newPassword: string) => {
                        formik.setFieldValue('password', newPassword);
                      }}
                      onBlur={formik.handleBlur('password')}
                      showPassword={showPassword}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          formik.handleSubmit();
                        }
                      }}
                      isLogin={true}
                      placeholder='Enter new password'
                    />
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        setShowPassword(!showPassword);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      {!showPassword && <img src={passwordOff} alt='passwordOff' />}
                      {showPassword && <img src={passwordOn} alt='passwordOn' />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className={styles.emailError}>
                      <span>{formik.errors.password}</span>
                    </div>
                  )}
                </div>
                <div className={'inputWrapper'} title='Must match the password exactly'>
                  <div className={'label'}>
                    Confirm new password<span className={styles.mandatory}>*</span>
                  </div>
                  <div
                    className={clsx(
                      formik.touched.changePassword && formik.errors.changePassword
                        ? styles.isInvalid
                        : '',
                      formik.touched.changePassword && !formik.errors.changePassword
                        ? styles.valid
                        : '',
                      'inputField'
                    )}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                      }
                    }}
                  >
                    <PasswordInput
                      value={formik.values.changePassword}
                      onChange={(newPassword: string) => {
                        formik.setFieldValue('changePassword', newPassword);
                      }}
                      onBlur={formik.handleBlur('changePassword')}
                      showPassword={showConfirmPassword}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          formik.handleSubmit();
                        }
                      }}
                      isLogin={true}
                      placeholder='Confirm new password'
                    />
                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    >
                      {!showConfirmPassword && <img src={passwordOff} alt='passwordOff' />}
                      {showConfirmPassword && <img src={passwordOn} alt='passwordOn' />}
                    </button>
                  </div>
                  {formik.touched.changePassword && formik.errors.changePassword && (
                    <div className={styles.emailError}>
                      <div className='fv-help-block'>
                        <span>{formik.errors.changePassword}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='d-grid'>
            <button
              type='submit'
              disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
              className={`${styles.submitBtn} ${
                (formik.isSubmitting || !formik.isValid || !formik.dirty) && styles.disableBtn
              }`}
            >
              {!loading && <span className='indicator-label'>Change password</span>}
              {loading && (
                <span className={styles.indicatorProgress} style={{ display: 'block' }}>
                  Please wait...
                  <span className={styles.spinner}></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.main>
  );
}
